// ---------------------------------------------------------------------------
// The frame the Design tab currently has selected.
//
// Canvas and Components are two screens over the same design file, so the
// selection cannot live inside either of them: picking a frame on one and
// switching to the other has to keep it. It is keyed by source id so a
// different imported file never inherits a selection that belonged to the
// previous one.
// ---------------------------------------------------------------------------

import { useSyncExternalStore } from "react";

interface SourceSurfaceSelection {
  sourceId: string;
  surfaceId: string;
}

const EMPTY: SourceSurfaceSelection = { sourceId: "", surfaceId: "" };
let selection: SourceSurfaceSelection = EMPTY;
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): SourceSurfaceSelection {
  return selection;
}

export function setSelectedSourceSurface(
  sourceId: string,
  surfaceId: string,
): void {
  if (selection.sourceId === sourceId && selection.surfaceId === surfaceId)
    return;
  selection = { sourceId, surfaceId };
  for (const listener of [...listeners]) listener();
}

/** `""` whenever the stored selection belongs to a different design file. */
export function useSelectedSourceSurface(sourceId: string): string {
  const current = useSyncExternalStore(subscribe, snapshot, snapshot);
  return current.sourceId === sourceId ? current.surfaceId : "";
}

/** Test seam — production code never needs to clear the selection wholesale. */
export function resetSelectedSourceSurface(): void {
  selection = EMPTY;
  for (const listener of [...listeners]) listener();
}
